import { useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { TrendingDown, DollarSign, Calendar, ArrowRight } from "lucide-react";

export const Calculator = () => {
  const [billValue, setBillValue] = useState(400);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const formatCurrency = (val) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(val);

  const handleSimulate = useCallback(() => {
    setLoading(true);
    setTimeout(() => {
      const discount = billValue <= 1000 ? 0.20 : billValue <= 2000 ? 0.25 : 0.30;
      setResult({
        bill_value: billValue,
        discount_percent: discount * 100,
        savings_monthly: billValue * discount,
        savings_yearly: billValue * discount * 12,
      });
      setLoading(false);
    }, 500);
  }, [billValue]);

  const scrollToContact = () => {
    const el = document.querySelector("#contato");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="simulador" data-testid="calculator-section" className="py-20 md:py-28 bg-white relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14">

          <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider bg-[#1a4fa0]/10 text-[#1a4fa0] mb-4">
            Simulador de Economia
          </span>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-4">
            Descubra quanto você pode <span className="text-[#1a4fa0]">economizar</span>
          </h2>
          <p className="text-base md:text-lg text-slate-500 max-w-xl mx-auto">
            Insira o valor da sua conta de luz e veja sua economia mensal e anual com a Solisy.
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}>

            <Card className="border-0 shadow-2xl shadow-[#1a4fa0]/10 rounded-3xl overflow-hidden">
              <CardContent className="p-8 md:p-12">
                <div className="mb-10">
                  <label className="block text-sm font-semibold text-slate-700 mb-6">
                    Valor mensal da sua conta de luz
                  </label>

                  <div className="text-center mb-8">
                    <span
                      data-testid="calculator-bill-display"
                      className="text-5xl md:text-6xl font-extrabold counter-value"
                      style={{ color: "#1a4fa0" }}>
                      {formatCurrency(billValue)}
                    </span>
                    <p className="text-sm text-slate-400 mt-2">por mês</p>
                  </div>

                  <div className="solisy-slider px-2">
                    <Slider
                      data-testid="calculator-slider"
                      min={150}
                      max={3000}
                      step={50}
                      value={[billValue]}
                      onValueChange={(val) => setBillValue(val[0])} />
                  </div>
                  <div className="flex justify-between mt-3 text-xs text-slate-400 font-medium">
                    <span>R$ 150</span>
                    <span>R$ 3.000</span>
                  </div>
                </div>

                <div className="text-center mb-8">
                  <Button
                    data-testid="calculator-simulate-btn"
                    onClick={handleSimulate}
                    disabled={loading}
                    className="rounded-full bg-[#f5b731] hover:bg-[#e0a31f] text-[#0f172a] h-14 px-12 text-lg font-bold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-transform duration-200">
                    {loading ? "Calculando..." : "Calcular Economia"}
                  </Button>
                </div>

                {result &&
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    data-testid="calculator-results">

                    <div className="border-t border-slate-100 pt-8">
                      <h3 className="text-center text-lg font-bold text-slate-800 mb-6">
                        Sua economia estimada com a Solisy
                      </h3>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-[#1a4fa0]/5 rounded-2xl p-6 text-center">
                          <TrendingDown className="w-8 h-8 mx-auto mb-3" style={{ color: "#1a4fa0" }} />
                          <p className="text-sm text-slate-500 mb-1">Seu Desconto</p>
                          <p className="text-2xl font-extrabold" style={{ color: "#1a4fa0" }}>
                            {result.discount_percent}%
                          </p>
                        </div>

                        <div className="bg-[#22c55e]/5 rounded-2xl p-6 text-center">
                          <DollarSign className="w-8 h-8 mx-auto mb-3 text-emerald-600" />
                          <p className="text-sm text-slate-500 mb-1">Economia Mensal</p>
                          <p className="text-2xl font-extrabold text-emerald-600">
                            {formatCurrency(result.savings_monthly)}
                          </p>
                        </div>

                        <div className="bg-[#f5b731]/10 rounded-2xl p-6 text-center">
                          <Calendar className="w-8 h-8 mx-auto mb-3" style={{ color: "#e0a31f" }} />
                          <p className="text-sm text-slate-500 mb-1">Economia Anual</p>
                          <p className="text-2xl font-extrabold" style={{ color: "#e0a31f" }}>
                            {formatCurrency(result.savings_yearly)}
                          </p>
                        </div>
                      </div>

                      <div className="text-center mt-8">
                        <Button
                          data-testid="calculator-contact-btn"
                          onClick={scrollToContact}
                          className="rounded-full bg-[#1a4fa0] hover:bg-[#153e80] text-white h-12 px-8 text-base font-semibold shadow-lg">
                          Quero esse desconto
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                }
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
